"use client"

import { useEffect, useId, useMemo, useRef, useState } from "react"

import { getOriginCities } from "@/lib/travel-api"
import { cn } from "@/lib/utils"

const MAX_ROWS = 8

/** Lowercase, accents off: "Forlì" and "forli" match each other. */
function fold(value: string): string {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim()
}

/**
 * Dentx 261016 — "Flying from" with suggestions: the cities already typed on
 * trips, most used first, so a report later counts one Milano instead of three
 * spellings. Follows HotelCombobox's interaction contract (search-as-you-type,
 * arrow/enter navigation, options preventDefault on mousedown so the click
 * beats the blur).
 *
 * Free text is never blocked — a city nobody has typed yet saves as typed and
 * becomes a suggestion from then on. If the list cannot be read the field is
 * the plain input it replaced.
 */
export function CityCombobox({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: string
  onChange: (city: string) => void
  placeholder: string
  disabled?: boolean
}) {
  const [cities, setCities] = useState<string[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  useEffect(() => {
    let cancelled = false
    getOriginCities()
      .then((list) => {
        if (!cancelled) setCities(list)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const rows = useMemo(() => {
    const q = fold(value)
    // Nothing typed: the most used cities, in the server's order.
    if (!q) return cities.slice(0, MAX_ROWS)
    // The exact spelling already in the box needs no row; a different
    // spelling of the same city ("milano" typed, "Milano" known) keeps its row
    // so one click corrects it.
    const candidates = cities.filter((city) => city !== value.trim())
    const starts = candidates.filter((city) => fold(city).startsWith(q))
    const contains = candidates.filter((city) => !fold(city).startsWith(q) && fold(city).includes(q))
    return [...starts, ...contains].slice(0, MAX_ROWS)
  }, [cities, value])

  const showList = open && rows.length > 0

  const choose = (city: string | undefined) => {
    if (city === undefined) return
    onChange(city)
    setOpen(false)
    inputRef.current?.blur()
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        value={value}
        disabled={disabled}
        maxLength={100}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        onChange={(e) => {
          onChange(e.target.value)
          setActive(0)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (!showList) return
          if (e.key === "ArrowDown") {
            setActive((a) => Math.min(a + 1, rows.length - 1))
            e.preventDefault()
          } else if (e.key === "ArrowUp") {
            setActive((a) => Math.max(a - 1, 0))
            e.preventDefault()
          } else if (e.key === "Enter") {
            choose(rows[active])
            e.preventDefault()
          } else if (e.key === "Escape") {
            setOpen(false)
            e.preventDefault()
          }
        }}
        placeholder={placeholder}
        autoComplete="off"
        className={cn(
          "h-[34px] w-full min-w-0 rounded-md border border-input bg-transparent px-3 text-[13px] outline-none",
          "placeholder:text-text-dim/60 focus:border-primary focus:ring-2 focus:ring-primary/15",
          disabled && "opacity-50"
        )}
      />

      {showList && (
        <div
          id={listId}
          role="listbox"
          className="absolute inset-x-0 top-[calc(100%+4px)] z-30 max-h-60 overflow-auto rounded-lg border border-border bg-popover p-1 shadow-lg"
        >
          {rows.map((city, index) => (
            <button
              key={city}
              type="button"
              role="option"
              aria-selected={index === active}
              onMouseDown={(e) => {
                e.preventDefault()
                choose(city)
              }}
              onMouseEnter={() => setActive(index)}
              className={cn(
                "flex w-full items-center rounded-md px-2.5 py-1.5 text-left text-[13px]",
                index === active && "bg-muted"
              )}
            >
              <span className="min-w-0 flex-1 truncate">{city}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
